import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFlow, deleteFlow, duplicateFlow, updateFlow, updateFlowStatus } from '@/lib/api/email-flows';
import { QUERY_KEYS } from '@/lib/constants';
import type { CreateFlowPayload, UpdateFlowPayload } from '@/lib/api/email-flows/types';

/**
 * Mutation hook for creating a new flow
 */
export function useCreateFlow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (flowData: CreateFlowPayload) => createFlow(flowData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });
        },
    });
}

/**
 * Mutation hook for updating a flow
 */
export function useUpdateFlow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (flowData: UpdateFlowPayload) => updateFlow(flowData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });

            if (variables.flow_id) {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_DETAILS, variables.flow_id] });
            }
        },
    });
}

/**
 * Mutation hook for updating flow status
 */
export function useUpdateFlowStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ flowId, status }: { flowId: number; status: string }) => updateFlowStatus(flowId, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });
        },
    });
}

/**
 * Mutation hook for duplicating a flow
 * Selectively invalidates only the relevant query based on flow type
 */
export function useDuplicateFlow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (flowId: number) => duplicateFlow(flowId),
        onSuccess: async data => {
            // type 1 = EVENT_TRIGGERED (flows), type 2 = USER_TRIGGERED (broadcasts)
            const isBroadcast = data.type === 2;
            const queryKey = isBroadcast ? QUERY_KEYS.BROADCASTS : QUERY_KEYS.EMAIL_FLOWS_LIST;

            await queryClient.invalidateQueries({ queryKey: [queryKey] });
        },
    });
}

/**
 * Mutation hook for deleting a flow
 * Note: Optimistic updates are disabled to allow for delete animation to play
 */
export function useDeleteFlow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (flowId: number) => deleteFlow(flowId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });
        },
    });
}
